import { isOnline, showToast } from '@beatq/shared';
import { enqueueFloorWrite, type FloorWriteKind } from '../floor-mode/floorWriteQueue.js';

export class FloorWriteQueuedError extends Error {
  constructor(message = 'Action queued for sync when back online') {
    super(message);
    this.name = 'FloorWriteQueuedError';
  }
}

export function isFloorWriteQueued(err: unknown): boolean {
  return (
    err instanceof FloorWriteQueuedError ||
    (err as { name?: string })?.name === 'FloorWriteQueuedError'
  );
}

function isNetworkFailure(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  const msg = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  return msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed');
}

let replayingQueue = false;

export function setReplayingFloorQueue(active: boolean): void {
  replayingQueue = active;
}

export async function floorAwareWrite<T>(
  input: {
    kind: FloorWriteKind;
    label: string;
    orderId?: string;
    itemId?: string;
    payload?: Record<string, unknown>;
  },
  execute: () => Promise<T>,
): Promise<T> {
  if (replayingQueue) {
    return execute();
  }
  if (!isOnline()) {
    enqueueFloorWrite({
      kind: input.kind,
      label: input.label,
      orderId: input.orderId,
      itemId: input.itemId,
      payload: input.payload ?? {},
    });
    showToast('info', `${input.label} queued — will sync when back online`);
    throw new FloorWriteQueuedError();
  }
  try {
    return await execute();
  } catch (err) {
    if (isNetworkFailure(err)) {
      enqueueFloorWrite({
        kind: input.kind,
        label: input.label,
        orderId: input.orderId,
        itemId: input.itemId,
        payload: input.payload ?? {},
      });
      showToast('info', `${input.label} queued — will sync when back online`);
      throw new FloorWriteQueuedError();
    }
    throw err;
  }
}
